import { useEffect, useMemo } from "@bestowinc/tinkertoy-shell-deps/react";
import { RouterProvider } from "react-router";

import { InternalTinkertoyProvider } from "./InternalTinkertoyProvider";
import { createLoadingSlot } from "./loading/loading-slot";
import { markShellBootstrapComplete } from "./performance-telemetry";
import { createShellRouter } from "./router/create-router";
import type { ShellConfig } from "./router/types";

/**
 * Root UI for the Tinkertoy V2 shell.
 *
 * Sets up the React Router instance with a single catch-all route whose loader
 * fetches view data from the Tinkertoy server. The router is created once and
 * memoized for the lifetime of the app.
 *
 * Responsibility split:
 *   - {@link InternalTinkertoyProvider} owns every cross-cutting provider
 *     (theme, module loader, future additions) so tests and the real app
 *     share a single wiring.
 *   - `ShellRoot` itself only stacks the real browser `RouterProvider` on
 *     top — tests substitute their own (memory) router inside the same
 *     internal provider.
 */
export function ShellRoot({ config }: { config: ShellConfig }) {
  // The router's loader and the shell's components share one loading slot;
  // create them together so StrictMode's discarded memo pass stays paired.
  const { router, loadingSlot } = useMemo(() => {
    const slot = createLoadingSlot();
    return { router: createShellRouter(config, slot), loadingSlot: slot };
  }, [config]);

  useEffect(() => {
    markShellBootstrapComplete();
  }, []);

  return (
    <InternalTinkertoyProvider
      theme={config.theme}
      moduleLoader={config.moduleLoader}
      viewLoader={config.viewLoader}
      apiBaseUrl={config.apiBaseUrl}
      developerTools={config.developerTools}
      kindredConfig={config.kindredConfig}
      loadingSlot={loadingSlot}
    >
      <RouterProvider router={router} />
    </InternalTinkertoyProvider>
  );
}
